import React, { useState } from "react";
import { Loader, Group, Menu, Modal, Textarea } from "@mantine/core";
import { updateAttendenceStaff } from "../../../services/staff";
import Icon from "./icons";
import { photoOf } from "./scheduleUtils";
import css from "./Scheduler.module.css";

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const fmtHrs = (sec) => (sec ? (sec / 3600).toFixed(1) : "0.0");

// Aggregate the per-shift /staff/manage-attendance data into per-caregiver
// rows (shifts, hours, regular/OT, exceptions, status) for the prototype's
// timesheets design. Exported so the page can also compute the KPI totals.
//
// `shifts` (optional) are the scheduled shifts for the same period. Joining
// them to the clock records (by the shift's trackingId) lets the timesheet
// compare scheduled vs actual and surface discrepancies — late clock-ins and
// missed shifts (no-show). This join is done at read time; nothing persists it.
export const aggregateTimesheets = (attendanceStaff, otThreshold = 40, shifts = []) => {
  const data = attendanceStaff?.attendanceStaff || [];

  // Link a clock record to its scheduled shift, and collect missed shifts per
  // staff. A missed shift is a no-show the admin hasn't excused.
  const shiftByTracking = new Map();
  const missedByStaff = new Map();
  const staffMeta = new Map();
  (shifts || []).forEach((s) => {
    if (s?.kind === "appointment") return;
    const sid = s?.staff?._id || s?.staff?.id;
    if (sid && s?.staff) staffMeta.set(String(sid), s.staff);
    if (s?.trackingId) shiftByTracking.set(String(s.trackingId), s);
    const isMissed = !s?.lateExcused && (s?.status === "absent" || s?.exc === "no-show");
    if (isMissed && sid) {
      if (!missedByStaff.has(String(sid))) missedByStaff.set(String(sid), []);
      missedByStaff.get(String(sid)).push(s);
    }
  });

  const byStaff = new Map();
  const ensure = (sid, seed) => {
    if (!byStaff.has(sid)) byStaff.set(sid, { staffId: sid, tracks: [], ...seed });
    return byStaff.get(sid);
  };
  data.forEach((t) => {
    const sid = t?.staff?.id || t?.staff?._id;
    if (!sid) return;
    // Drafts (not yet submitted by staff) are not visible to the admin.
    if (t?.submitted === false) return;
    const g = ensure(String(sid), {
      name: `${t.staff.firstName || t.staff.name || ""} ${t.staff.lastName || ""}`.trim() || "Staff",
      photo: photoOf(t.staff.photo || t.staff.profilePicture || t.staff.image || t.staff.imageurl),
      role: t.staff.position || t.staff.designation || t.staff.jobTitle || "Caregiver",
    });
    // Attach the linked shift + a late flag so the detail drawer can show
    // scheduled vs actual without another lookup.
    const shift = shiftByTracking.get(String(t._id)) || null;
    g.tracks.push({ ...t, shift, late: !!(shift && shift.exc === "late" && !shift.lateExcused) });
  });

  // Staff who only have missed shifts (never clocked in) still need a row so
  // the no-show surfaces instead of vanishing from an attendance-only view.
  missedByStaff.forEach((arr, sid) => {
    const m = staffMeta.get(sid) || {};
    ensure(sid, {
      name: `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.name || "Staff",
      photo: photoOf(m.photo || m.profilePicture || m.image || m.imageurl),
      role: m.position || m.designation || m.jobTitle || "Caregiver",
    });
  });

  return Array.from(byStaff.values()).map((g) => {
    const totalSec = g.tracks.reduce((a, t) => a + (Number(t.total_work_time) || 0), 0);
    const hours = totalSec / 3600;
    const missing = g.tracks.filter((t) => !t.endTime).length; // missing clock-out
    const lateCount = g.tracks.filter((t) => t.late).length;
    const missed = missedByStaff.get(g.staffId) || [];
    const anyRejected = g.tracks.some((t) => t.approvedStatus === "rejected");
    const allApproved = g.tracks.length > 0 && g.tracks.every((t) => t.approvedStatus === "approved");
    return {
      ...g,
      shifts: g.tracks.length,
      hours,
      regular: Math.min(otThreshold, hours),
      ot: Math.max(0, hours - otThreshold),
      // Exceptions now span all attendance discrepancies, not just missing
      // clock-outs: missing clock-out + late clock-in + missed shift.
      exceptions: missing + lateCount + missed.length,
      missing,
      lateCount,
      missed,
      status: anyRejected ? "rejected" : allApproved ? "approved" : "pending",
      // Entries an admin can still act on (approve or reject).
      pending: g.tracks.filter((t) => t.approvedStatus !== "approved"),
      first: g.tracks[0],
    };
  });
};

// Presentational timesheets table — receives aggregated rows. Reuses the
// existing view/edit modal + update-attendance endpoint, so it does not touch
// the shared StaffAttendanceTable used elsewhere.
export default function TimesheetsTable({ rows = [], loading, onRowClick, onApproved, headerLeft = null, headerExport = null, headerPrimary = null, showOvertime = true }) {
  const [selected, setSelected] = useState(() => new Set());
  const [approving, setApproving] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Reject a caregiver's submitted entries with a reason (sent back to staff).
  const confirmReject = async () => {
    const payload = (rejectTarget?.pending || []).map((t) => ({ id: t._id, approvedStatus: "rejected", rejectionReason: rejectReason.trim() }));
    if (!payload.length) { setRejectTarget(null); return; }
    setApproving(true);
    await updateAttendenceStaff(payload);
    setApproving(false);
    setRejectTarget(null);
    setRejectReason("");
    onApproved?.();
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSel = rows.length > 0 && rows.every((r) => selected.has(r.staffId));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(rows.map((r) => r.staffId)));

  const approveSelected = async () => {
    const payload = rows
      .filter((r) => selected.has(r.staffId))
      .flatMap((r) => r.pending.map((t) => ({ id: t._id, approvedStatus: "approved" })));
    if (!payload.length) return;
    setApproving(true);
    await updateAttendenceStaff(payload);
    setApproving(false);
    setSelected(new Set());
    onApproved?.();
  };

  // Approve a single caregiver's pending entries (from the row kebab menu).
  const approveRow = async (r) => {
    const payload = (r.pending || []).map((t) => ({ id: t._id, approvedStatus: "approved" }));
    // There's an issue when there's nothing to approve (e.g. a no-show shift with
    // no timesheet entry) or an entry is still missing its clock-out. Open the
    // detail side panel so the admin can review/resolve it, rather than the
    // action silently doing nothing.
    const hasIssue = payload.length === 0 || (r.pending || []).some((t) => !t.endTime);
    if (hasIssue) { onRowClick?.(r.staffId, r.first?._id, "view"); return; }
    setApproving(true);
    await updateAttendenceStaff(payload);
    setApproving(false);
    onApproved?.();
  };

  const selCount = rows.filter((r) => selected.has(r.staffId)).length;

  return (
    <div className={css.tblCard}>
      <div className={css.tblHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <h3 style={{ margin: 0 }}>Timesheets</h3>
          {headerLeft}
        </div>
        <Group spacing={8}>
          {headerExport}
          {selCount > 0 && (
            <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} style={{ height: 36 }} onClick={approveSelected} disabled={approving}>
              <Icon name="check" size={15} /> Approve ({selCount})
            </button>
          )}
          {headerPrimary}
        </Group>
      </div>
      <div className={css.tblScroll}>
        {loading ? (
          <Group position="center" p={60}><Loader color="var(--app-primary)" /></Group>
        ) : (
          <table className={css.tbl}>
            <thead>
              <tr>
                <th className={css.ckCol}>
                  <button type="button" className={`${css.ckbox} ${allSel ? css.on : ""}`} onClick={toggleAll} aria-label="Select all">
                    {allSel ? <Icon name="check" size={12} /> : null}
                  </button>
                </th>
                <th>Caregiver</th>
                <th>Shifts</th>
                <th>Hours logged</th>
                <th>Regular</th>
                <th>Overtime</th>
                <th>Exceptions</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9}>
                    <div className={css.empty2}>
                      <Icon name="clock" size={38} />
                      <h4>No timesheets yet</h4>
                      <p>Hours appear here as staff clock out.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.staffId} className={css.click} onClick={() => onRowClick?.(r.staffId, r.first?._id, "view")}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button type="button" className={`${css.ckbox} ${selected.has(r.staffId) ? css.on : ""}`} onClick={() => toggle(r.staffId)} aria-label="Select">
                        {selected.has(r.staffId) ? <Icon name="check" size={12} /> : null}
                      </button>
                    </td>
                    <td>
                      <div className={css.cellStaff}>
                        <span className={css.cellAv}>
                          {r.photo ? <img src={r.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} /> : initials(r.name)}
                        </span>
                        <span className={css.cellNm}>{r.name}</span>
                      </div>
                    </td>
                    <td className={css.numStrong}>{r.shifts}</td>
                    <td className={css.numStrong}>{fmtHrs(r.hours * 3600)}h</td>
                    <td>{r.regular.toFixed(1)}h</td>
                    <td>{showOvertime && r.ot > 0 ? <span className={`${css.badge2} ${css.amber}`}>{r.ot.toFixed(1)}h OT</span> : <span style={{ color: "var(--app-text-muted)" }}>-</span>}</td>
                    <td>{r.exceptions ? <span className={`${css.badge2} ${css.red}`}><Icon name="alert" size={11} /> {r.exceptions}</span> : <span style={{ color: "var(--app-text-muted)" }}>-</span>}</td>
                    <td>
                      <span className={`${css.badge2} ${r.status === "approved" ? css.green : r.status === "rejected" ? css.red : css.amber}`}>
                        {r.status === "approved" ? "Approved" : r.status === "rejected" ? "Rejected" : "Pending"}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={css.rowActions}>
                        <Menu shadow="md" width={150} position="bottom-end">
                          <Menu.Target>
                            <button type="button" className={css.kebabBtn} aria-label="Actions"><Icon name="dotsV" size={16} /></button>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => onRowClick?.(r.staffId, r.first?._id, "view")}>View</Menu.Item>
                            {r.status !== "approved" && <Menu.Item onClick={() => approveRow(r)} disabled={approving}>Approve</Menu.Item>}
                            {r.status !== "rejected" && <Menu.Item color="red" onClick={() => setRejectTarget(r)} disabled={approving}>Reject</Menu.Item>}
                          </Menu.Dropdown>
                        </Menu>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        opened={!!rejectTarget}
        onClose={() => { setRejectTarget(null); setRejectReason(""); }}
        title={`Reject ${rejectTarget?.name || ""}'s timesheet`}
        centered
        radius={14}
        zIndex={400}
      >
        <Textarea
          label="Reason for rejection"
          description="Shared with the staff member so they can fix and resubmit."
          placeholder="e.g. Clock-out time looks incorrect. Please review and resubmit."
          minRows={3}
          autosize
          value={rejectReason}
          onChange={(e) => setRejectReason(e.currentTarget.value)}
        />
        <Group position="right" mt={16}>
          <button type="button" className={css.tbBtn} onClick={() => { setRejectTarget(null); setRejectReason(""); }}>Cancel</button>
          <button type="button" className={`${css.tbBtn} ${css.tbBtnPrimary}`} onClick={confirmReject} disabled={approving || !rejectReason.trim()}>
            <Icon name="x" size={15} /> Reject timesheet
          </button>
        </Group>
      </Modal>
    </div>
  );
}
